/**
 * Utilidades para manejo y validación de tokens JWT con issuer específico
 */

import { getMsalInstance } from "../../config/msalInstance";
import { loginRequest } from "../../config/authConfig";

/**
 * Decodifica un token JWT sin verificar la firma
 */
export function decodeJWT(token: string): any {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
}

/**
 * Obtiene un token con configuración específica para forzar el issuer deseado
 */
export async function getTokenWithSpecificIssuer(): Promise<string | null> {
  try {
    const msalInstance = getMsalInstance();
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error("No active account found");
    }

    // Intentar con diferentes configuraciones de scopes y authority
    const tokenRequest = {
      ...loginRequest,
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    return response.idToken;
  } catch (error) {
    console.error("Error getting token with specific issuer:", error);
    return null;
  }
}

/**
 * Valida si un token tiene el issuer esperado
 */
export function validateTokenIssuer(
  token: string,
  expectedIssuer: string = "https://login.microsoftonline.com/99d911b9-6dc3-401c-9398-08fc6b377b74/v2.0"
): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.iss) {
    return false;
  }

  console.log("Token issuer:", decoded.iss);
  console.log("Expected issuer:", expectedIssuer);

  // Verificar si el issuer coincide exactamente
  return decoded.iss === expectedIssuer;
}

/**
 * Obtiene información detallada del token para debugging
 */
export function getTokenInfo(token: string): any {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return null;
  }

  return {
    issuer: decoded.iss,
    audience: decoded.aud,
    subject: decoded.sub,
    tenantId: decoded.tid,
    version: decoded.ver,
    issuedAt: new Date(decoded.iat * 1000),
    expiresAt: new Date(decoded.exp * 1000),
    fullPayload: decoded,
  };
}

/**
 * Función para obtener token con validación de issuer
 */
export async function getTokenWithValidation(): Promise<{
  token: string | null;
  isValidIssuer: boolean;
  tokenInfo: any;
}> {
  try {
    const token = await getTokenWithSpecificIssuer();
    if (!token) {
      return {
        token: null,
        isValidIssuer: false,
        tokenInfo: null,
      };
    }

    const isValidIssuer = validateTokenIssuer(token);
    const tokenInfo = getTokenInfo(token);

    return {
      token,
      isValidIssuer,
      tokenInfo,
    };
  } catch (error) {
    console.error("Error in getTokenWithValidation:", error);
    return {
      token: null,
      isValidIssuer: false,
      tokenInfo: null,
    };
  }
}
